import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Table, Button, Input, Spin, message, Dropdown, Menu, Modal } from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { PhoneOutlined, FilterOutlined, DownOutlined } from "@ant-design/icons";
import Select from "react-select";
import Swal from "sweetalert2";
import exportService from "./utils/exportService"; // Import the actual exportService
import UserService from "./utils/userService";
import { renderDate } from "./utils/dateFormatter";
import PrintReportPreview from "./reports/PrintReportPreview";
import PartnerService from "./utils/partnerService";
import DatePicker from "react-bootstrap-daterangepicker"; // Import DatePicker
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS
import "bootstrap-daterangepicker/daterangepicker.css"; // Import DatePicker CSS

const ReservationList = () => {
  const [searchText, setSearchText] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const { Search } = Input;
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const business_date = localStorage.getItem("business_date") || moment().format("YYYY-MM-DD"); // Default to today if business_date is invalid
  const businessMoment = moment(business_date).isValid() ? moment(business_date).startOf("day") : moment().startOf("day");
  const [dateRange, setDateRange] = useState(() => ({
    startDate: businessMoment,
    endDate: moment(businessMoment).endOf("month"),
  }));
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);

  const { getFullname } = UserService();
  const { getPartnerName } = PartnerService();

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error("Failed to enter fullscreen:", err);
        });
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen:", err);
        });
        setFullScreen(false);
      }
    };

    const handleFullscreenChange = () => {
      setFullScreen(!!document.fullscreenElement);
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener("click", handleClick);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener("click", handleClick);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen during cleanup:", err);
        });
      }
    };
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [dateRange]);

  const fetchReservations = async () => {
    if (!dateRange.startDate.isValid() || !dateRange.endDate.isValid()) {
      message.error("Invalid date range provided.");
      setReservations([]);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const response = await fetch(
        `${apiURL}/booking/reservations?islist=true&startDate=${dateRange.startDate.format("YYYY-MM-DD")}&endDate=${dateRange.endDate.format("YYYY-MM-DD")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch reservations.");
      }

      const data = await response.json();
      if (data.success) {
        setReservations(data.reservations);
      } else {
        throw new Error("No reservations found.");
      }
    } catch (err) {
      setError(err.message);
      Swal.fire({
        title: "Error!",
        text: err.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredReservations = reservations.filter((reservation) => {
    const matchesSearch =
      reservation.booking_number.toLowerCase().includes(searchText.toLowerCase()) ||
      reservation.guest_name.toLowerCase().includes(searchText.toLowerCase()) ||
      reservation.guest_email.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus = !statusFilter || reservation.booking_status === statusFilter.value;
    const matchesSource = !sourceFilter || reservation.booking_source === sourceFilter.value;

    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleDateChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "month").startOf("month"),
            endDate: prevRange.startDate.clone().subtract(1, "month").endOf("month"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "month").startOf("month"),
            endDate: prevRange.startDate.clone().add(1, "month").endOf("month"),
          };
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        const newStart = moment(start).startOf("day");
        const newEnd = moment(end).startOf("day");
        const currentBusiness = moment(business_date).startOf("day");

        if (newStart.isBefore(currentBusiness)) {
          message.error("Cannot select dates before the current business date.");
          return prevRange;
        }

        newRange = { startDate: newStart, endDate: newEnd };
      }

      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        message.error("Invalid date selected.");
        setReservations([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        message.error("Start date cannot be after end date.");
        setReservations([]);
        return prevRange;
      }

      return newRange;
    });
  };

  const secretKey = "abde100199199111";

  const statusOptions = [
    { value: "reserved", label: "Reserved" },
    { value: "checked_in", label: "Checked In" },
    { value: "checked_out", label: "Checked Out" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const sourceOptions = [
    { value: "Direct", label: "Direct" },
    { value: "Online", label: "Online" },
    { value: "Agency", label: "Agency" },
  ];

  const handleFilterApply = () => {
    // No need to close modal manually; Bootstrap handles it via data-bs-dismiss
  };

  const handleFilterReset = () => {
    setStatusFilter(null);
    setSourceFilter(null);
    setSearchText("");
  };

  const handleCloseModal = () => {
    setPdfVisible(false);
    setPdfData(null);
  };

  const exportHeaders = [
    { key: "guest_name", title: "Guest Name" },
    { key: "guest_phone", title: "Guest Phone" },
    { key: "expected_time", title: "Expected Time" },
    { key: "booking_number", title: "Reservation No" },
    { key: "booking_status", title: "Status" },
    { key: "booking_source", title: "Booking Source" },
    { key: "room_numbers", title: "Room No" },
    { key: "check_in_date", title: "Check-In" },
    { key: "check_out_date", title: "Check-Out" },
    { key: "nights", title: "Night" },
    { key: "reservation_type", title: "Reservation Type" },
    { key: "created_at", title: "Reservation Date" },
    { key: "guest_email", title: "Guest Email" },
    { key: "guest_nationality", title: "Guest Nationality" },
  ];

  const exportData = filteredReservations.map((reservation) => ({
    guest_name: reservation.guest_name,
    guest_phone: reservation.guest_phone || "N/A",
    expected_time: reservation.guest_phone || "N/A",
    booking_number: reservation.booking_number,
    booking_status: reservation.booking_status.replace("_", " ").toUpperCase(),
    booking_source: reservation.booking_source || "N/A",
    room_numbers: reservation.room_numbers || "N/A",
    check_in_date: moment(reservation.check_in_date).format("DD MMM YYYY"),
    check_out_date: moment(reservation.check_out_date).format("DD MMM YYYY"),
    nights: moment(reservation.check_out_date).diff(moment(reservation.check_in_date), "days") || 0,
    reservation_type: reservation.reservation_type,
    created_at: moment(reservation.created_at).format("YYYY-MM-DD"),
    guest_email: reservation.guest_email,
    guest_nationality: reservation.guest_nationality || "N/A",
  }));

  const totalReservations = filteredReservations.length;
  const totalNights = filteredReservations.reduce(
    (sum, reservation) =>
      sum + moment(reservation.check_out_date).diff(moment(reservation.check_in_date), "days"),
    0
  );
  const totalDirectBookings = filteredReservations.filter(
    (reservation) => reservation.booking_source === "Direct"
  ).length;

  const totalsForPageFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
  ];

  const exportFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
  ];

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData,
      exportHeaders,
      `reservation_list${suffix}.xlsx`,
      "Reservation List"
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData,
      exportHeaders,
      `reservation_list${suffix}.csv`
    );
  };

  const handleExportPDF = () => {
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      "Reservation List",
      `reservation_list${suffix}.pdf`,
      false,
      true,
      totalsForPageFooter,
      exportFooter
    );
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const pdfBlob = await exportService.exportToPDF(
        exportData,
        exportHeaders,
        "Reservation List",
        null,
        true,
        true,
        totalsForPageFooter,
        exportFooter
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPdfVisible(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      message.error("Failed to load PDF preview.");
    } finally {
      setLoading(false);
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      render: (name) => (
        <span className="colored-box guest-name-box" style={{ textAlign: "left" }}>{name}</span>
      ),
      sorter: (a, b) => a.guest_name.localeCompare(b.guest_name),
      width: 200,
      fixed: "left",
    },
    {
      title: "Guest Phone",
      dataIndex: "guest_phone",
      render: (phone) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          <PhoneOutlined /> {phone || "N/A"}
        </span>
      ),
      width: 150,
      fixed: "left",
    },
    {
      title: "Expected Time",
      dataIndex: "expected_time",
      render: (phone) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          <PhoneOutlined /> {phone || "N/A"}
        </span>
      ),
      width: 150,
      fixed: "left",
    },
    {
      title: "Reservation No",
      dataIndex: "booking_number",
      render: (booking_number, record) => {
        try {
          const encryptedId = CryptoJS.AES.encrypt(
            record.booking_id.toString(),
            secretKey
          ).toString();
          return (
            <Link
              to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
              className="colored-box reservation-no-box"
            >
              {booking_number}
            </Link>
          );
        } catch (error) {
          console.error("Encryption Error:", error);
          return <span className="colored-box reservation-no-box">Error</span>;
        }
      },
      sorter: (a, b) => a.booking_number.localeCompare(b.booking_number),
      width: 200,
      fixed: "left",
    },
    {
      title: "Status",
      dataIndex: "booking_status",
      render: (status) => (
        <span
          className={`colored-box status-box ${
            status === "reserved"
              ? "bg-primary"
              : status === "checked_in"
              ? "bg-success"
              : status === "asc"
              ? "bg-secondary"
              : status === "checked_out"
              ? "bg-secondary"
              : status === "cancelled"
              ? "bg-danger"
              : "bg-default"
          }`}
        >
          {status.replace("_", " ").toUpperCase()}
        </span>
      ),
      width: 150,
      fixed: "left",
    },
    {
      title: "Booking Source",
      dataIndex: "booking_source",
      render: (source) => (
        <span className="colored-box source-box">{getPartnerName(source) || "N/A"}</span>
      ),
      width: 150,
    },
    {
      title: "Room No",
      dataIndex: "room_numbers",
      render: (room_numbers) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {room_numbers || "N/A"}
        </span>
      ),
      width: 120,
    },
    {
      title: "Check-In",
      dataIndex: "check_in_date",
      render: (date) => (
        <span className="date-box check-in-box">{renderDate(date)}</span>
      ),
      sorter: (a, b) => new Date(a.check_in_date) - new Date(b.check_in_date),
      width: 150,
    },
    {
      title: "Check-Out",
      dataIndex: "check_out_date",
      render: (date) => (
        <span className="date-box check-out-box">{renderDate(date)}</span>
      ),
      sorter: (a, b) => new Date(a.check_out_date) - new Date(b.check_out_date),
      width: 150,
    },
    {
      title: "Night",
      dataIndex: "nights",
      render: (nights, record) => {
        const checkIn = moment(record.check_in_date);
        const checkOut = moment(record.check_out_date);
        const nightCount = checkOut.diff(checkIn, "days");
        return <span className="date-box night-box">{nightCount || 0}</span>;
      },
      sorter: (a, b) => {
        const nightsA = moment(a.check_out_date).diff(moment(a.check_in_date), "days");
        const nightsB = moment(b.check_out_date).diff(moment(b.check_in_date), "days");
        return nightsA - nightsB;
      },
      width: 110,
    },
    {
      title: "Reservation Type",
      dataIndex: "reservation_type",
      sorter: (a, b) => a.reservation_type.localeCompare(b.reservation_type),
      width: 170,
      render: (reservation_type) => {
        let className;
        if (reservation_type.toLowerCase() === "confirmed") {
          className = "date-box-confirmed";
        } else if (reservation_type.toLowerCase() === "tentative") {
          className = "date-box-tentative";
        } else {
          className = "date-box-default";
        }
        return (
          <span className={`date-box ${className}`}>
            {reservation_type}
          </span>
        );
      },
    },
    {
      title: "Reservation Date",
      dataIndex: "created_at",
      render: (date) => renderDate(date),
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
      width: 170,
    },
    {
      title: "Email",
      dataIndex: "guest_email",
      width: 250,
    },
    {
      title: "Nationality",
      dataIndex: "guest_nationality",
      width: 120,
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      width: 150,
      render: (text) => getFullname(text),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      render: (date) => renderDate(date),
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
      width: 120,
    },
    {
      title: "Cancelled By",
      dataIndex: "cancelled_by",
      width: 150,
      render: (userId) => getFullname(userId),
    },
    {
      title: "Checked-In By",
      dataIndex: "checked_in_by",
      width: 150,
      render: (userId) => getFullname(userId),
    },
   
    {
      title: "Updated By",
      dataIndex: "updated_by",
      width: 150,
      render: (userId) => getFullname(userId),
    },
  ];

  const getDynamicHeight = () => {
    return window.innerHeight - 150;
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title">Reservations</h3>
            </div>
            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <DatePicker
                    initialSettings={{
                      singleDatePicker: false,
                      showDropdowns: true,
                      startDate: dateRange.startDate.toDate(),
                      endDate: dateRange.endDate.toDate(),
                      minDate: businessMoment.toDate(), // Restrict past dates
                      ranges: {
                        "Today": [businessMoment.toDate(), businessMoment.toDate()],
                        "Tomorrow": [
                          businessMoment.clone().add(1, "day").toDate(),
                          businessMoment.clone().add(1, "day").toDate(),
                        ],
                        "This Week": [
                          businessMoment.clone().startOf("week").toDate(),
                          businessMoment.clone().endOf("week").toDate(),
                        ],
                        "This Month": [
                          businessMoment.toDate(),
                          businessMoment.clone().endOf("month").toDate(),
                        ],
                        "Next Month": [
                          businessMoment.clone().add(1, "month").startOf("month").toDate(),
                          businessMoment.clone().add(1, "month").endOf("month").toDate(),
                        ],
                      },
                      locale: {
                        format: "DD MMM YYYY",
                        applyLabel: "Select",
                        cancelLabel: "Cancel",
                      },
                    }}
                    onCallback={(start, end) => {
                      handleDateChange({ start, end });
                    }}
                  >
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                        style={{
                          width: "220px",
                          height: "38px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          textAlign: "center",
                          padding: "0 10px 0 10px",
                          borderRight: "none",
                          backgroundColor: "#ffffff",
                        }}
                        readOnly
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text"
                          style={{
                            height: "38px",
                            backgroundColor: "#fff",
                            borderLeft: "none",
                            borderRadius: "0 4px 4px 0",
                            cursor: "pointer",
                          }}
                        >
                          <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                        </span>
                      </div>
                    </div>
                  </DatePicker>
                </div>
                <div style={{ flexGrow: 1, maxWidth: 400 }}>
                  <Search
                    placeholder="Search by Name, Email, or Booking ID"
                    allowClear
                    enterButton="Search"
                    size="large"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center flex-shrink-0 gap-2">
                  <div className="view-icons d-flex align-items-center gap-1">
                    <Link to="#" className="btn btn-link" data-bs-toggle="modal" data-bs-target="#scrollable-modal">
                      <i className="las la-filter" />
                    </Link>
                    <Link to="#" className="btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                    <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                      <Button className="custom-btn-drop d-inline-flex align-items-center" type="text">
                        <i className="la la-download custom-icon me-2" /> Download <DownOutlined className="ms-2" />
                      </Button>
                    </Dropdown>
                  </div>
                  <Link to="/hospitality/guest-reservation" className="btn add-btn custom-btn ms-2">
                    <i className="la la-plus-circle custom-icon" /> Add Reservation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PrintReportPreview
          visible={pdfVisible}
          onClose={() => setPdfVisible(false)}
          reportTitle="Reservation List"
          propertyName="Your Property Name"
          data={exportData}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={["guests", "nights"]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={process.env.REACT_APP_API_URL}
        />

        <div
          className="table-responsive"
          style={{
            minHeight: "60vh",
            height: "calc(100vh - 150px)",
            overflow: "auto",
          }}
        >
          {loading ? (
            <div
              id="loader-wrapper-"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : error ? (
            <p
              className="text-danger text-center"
              style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {error}
            </p>
          ) : filteredReservations.length === 0 ? (
            <div
              className="no-data-container text-center mt-4"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                alt="No data available"
                style={{ width: "150px", marginBottom: "20px" }}
              />
              <h3>No Data Available</h3>
              <p>No reservations found for the selected date range.</p>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredReservations}
              pagination={{ pageSize: 20 }}
              className="table-striped custom-table"
              rowKey={(record) => record.id}
              scroll={{
                x: 2000,
                y: getDynamicHeight(),
              }}
            />
          )}
        </div>

        <div
          className="modal custom-modal fade"
          id="scrollable-modal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="filterModalTitle"
          aria-hidden="true"
        >
          <div
            className="modal-dialog modal-lg"
            style={{
              margin: 0,
              position: "fixed",
              right: 0,
              height: "100vh",
              width: "40%",
              zIndex: 1060,
            }}
            role="document"
          >
            <div className="modal-content h-100">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalTitle">
                  Filter
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
              >
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Status:</label>
                      <Select
                        options={statusOptions}
                        placeholder="Select Status"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "300px",
                            borderRadius: 4,
                            borderColor: '#d9d9d9',
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Booking Source:</label>
                      <Select
                        options={sourceOptions}
                        placeholder="Select Source"
                        value={sourceFilter}
                        onChange={setSourceFilter}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "300px",
                            borderRadius: 4,
                            borderColor: '#d9d9d9',
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleFilterReset}
                  data-bs-dismiss="modal"
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFilterApply}
                  data-bs-dismiss="modal"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationList;